import { FC } from 'react';
import { observer } from 'mobx-react';
import { usePlatform, ScreenOrientation } from '~/services/platform';
import { useOrientation } from '~/utils/hooks/useOrientation';
import { useStore } from '~/stores';
import { OrientationButtons, OrientationButton, AutoBadge } from './styles';

/** Ícone Paisagem (mesmo do painel MediaInfo). */
const LandscapeIcon: FC = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 5H3c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm-2 12H5V7h14v10z" />
  </svg>
);

/** Ícone Retrato (mesmo do painel MediaInfo). */
const PortraitIcon: FC = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M17 1H7c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-2-2-2zm-2 18H9V5h6v14z" />
  </svg>
);

/**
 * Botões Paisagem/Retrato pras telas de espera (NoContent).
 *
 * 2026-06-05 — Renato: nas telas "Aguardando conteúdo"/"Aguardando aprovação"
 * o operador precisa conseguir AJUSTAR a orientação na mão quando a
 * auto-detecção não acerta sozinha (ex: tablet montado em retrato reconhecido
 * como paisagem). Mesma ação do painel do player (`handleOrientationChange`):
 * persiste o override + propaga pro servidor + força re-sync do conteúdo da
 * orientação certa.
 *
 * Gate: só aparece onde a orientação é ajustável — `supportsOrientationChange()`
 * é `true` em Android/Browser e `false` em Tizen/webOS (TV é landscape-fixo).
 * Auto-contido (lê stores via `useStore`), reutilizável em qualquer tela azul.
 */
const OrientationControls: FC = observer(() => {
  const platform = usePlatform();
  const { authStore, syncStore } = useStore();
  const { detectedOrientation } = useOrientation();

  // TV (Tizen/webOS) é landscape-fixo — não renderiza nada.
  if (!platform.supportsOrientationChange()) return null;

  // Override pinado (selectedOrientation) vence; senão, a orientação física
  // detectada. Ambos reativos (observable + estado React) → highlight correto.
  const effective = authStore.selectedOrientation ?? detectedOrientation;

  const handleSelect = (orientation: ScreenOrientation): void => {
    // Clicar na orientação já auto-detectada limpa o override (volta pro auto).
    const override = orientation === detectedOrientation ? null : orientation;
    authStore.setSelectedOrientation(override);
    // Propaga pro servidor (pending ou aprovado) + re-sync do conteúdo certo.
    void authStore.updateOrientation(override ?? detectedOrientation);
    syncStore.forceResync();
  };

  return (
    <OrientationButtons>
      <OrientationButton
        $selected={effective === 'landscape'}
        onClick={() => handleSelect('landscape')}
        tabIndex={0}
        className="focusable"
        aria-label="Paisagem"
      >
        <LandscapeIcon />
        Paisagem
        {detectedOrientation === 'landscape' && <AutoBadge>Auto</AutoBadge>}
      </OrientationButton>
      <OrientationButton
        $selected={effective === 'portrait'}
        onClick={() => handleSelect('portrait')}
        tabIndex={0}
        className="focusable"
        aria-label="Retrato"
      >
        <PortraitIcon />
        Retrato
        {detectedOrientation === 'portrait' && <AutoBadge>Auto</AutoBadge>}
      </OrientationButton>
    </OrientationButtons>
  );
});

export default OrientationControls;
